/* eslint-disable */
import { BookOutlined, FileDoneOutlined, WarningOutlined } from '@ant-design/icons';
import type { TableColumnsType } from 'antd';
import {
  Button, Card,
  message,
  Modal, Space,
  Table, Tooltip, Typography
} from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import TextArea from 'antd/es/input/TextArea';
import { useAppSelector } from "app/store";
import { getAccessToken } from 'common/services/common/storage';
import { formatDateDDMMYYYY, formatNumber } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

export interface CalculateSalaryDataType {
  id: number;
  key: React.Key;
  order: number;
  name: string;
  position: string;
  phoneNumber: string;
  taxCode: string;
  socialInsurance: string;
  socialInsuranceTime: string;
  identityNumber: string;
  idCardIssuedAt: string;
  idCardIssuedBy: string;
  bankNumber: string;
  bankName: string;
  bankBranch: string;
  province: string;
  workedShift: number;
  workedDay: number;
  unitPrice: number;
  totalPrice: number;
  training: number;
  workOnHoliday: number;
  survey: number;
  phoneAllowance: number;
  goFar: number;
  setup: number;
  stay: number;
  eat: number;
  ot: number;
  warehouse: number;
  amountReceived: number;
  salaryWarning: boolean;
  salaryWarningYear: boolean;
  supportAnother: number;
  note?: string;
}

const SalaryCalculatingView: React.FC = () => {
  const navigate = useNavigate();
  const { roles: rolesUser } = useAppSelector((state) => state.auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';

  const { t } = useTranslation();
  const [isNoteOpen, setIsNoteOpen] = useState(false);

  const [jsonFile, setJsonFile] = useState<string>('');
  const [isLoadingJsonFile, setIsLoadingJsonFile] = useState<boolean>(false);
  const loadedJSonFileRef = useRef<boolean>(false);
  const usersRef = useRef<CalculateSalaryDataType[]>([]);

  const methodNote = useForm<{ note: string, id: number | null }>({
    defaultValues: {
      note: '',
      id: null
    },
    mode: 'onChange',
  });

  const streamEmployees = async (keyword?: string) => {
    try {
      setIsLoadingJsonFile(true);
      const token = getAccessToken();
      // request with form data to api for endpoint /api/v1/staffs/import
      const res = await fetch(`/api/v1/salary-records?_payrollPeriodId=${payrollPeriodId}&keyword=${keyword || ''}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        console.log('Fail with status: ', res?.status);
        if (res.status === 401) {
          navigate('/login');
        } else {
          message.error(t('message.uploadError'));
        }
        setIsLoadingJsonFile(false);
        return;
      }
      const reader = res.body?.getReader();

      let result;
      const decoder = new TextDecoder();
      if (reader) {
        do {
          // eslint-disable-next-line no-await-in-loop
          result = await reader.read();
          const record = decoder.decode(result.value, { stream: true });
          setJsonFile((prev) => prev + record);
        } while (!result.done);

        if (result.done) {
          loadedJSonFileRef.current = true;
          setIsLoadingJsonFile(false);
        }
      }
    } catch (e) {
      setIsLoadingJsonFile(false);
      // Emtry
    }
  };

  useEffect(() => {
    if (!getPermission(rolesUser, roles.SALARY_RECORD_INDEX)) return;

    if (!!payrollPeriodId) {
      streamEmployees();
    }
  }, [rolesUser]);

  const tableData = useMemo(() => {
    if (!loadedJSonFileRef.current) {
      return [];
    }
    if (jsonFile === '') {

      return [];
    }

    const lines = jsonFile.split('\n');
    const rowData: CalculateSalaryDataType[] = [];

    lines.forEach((line) => {
      if (line.trim()) {
        const json = JSON.parse(line);
        if (json.status === 'processing') {
          const { staffData, position, salaryData, bank } = json.data;
          rowData.push({
            id: staffData.id,
            key: staffData.id,
            name: staffData.fullname,
            identityNumber: staffData.idCardNumber,
            position: position?.name || '',
            phoneNumber: staffData.phoneNumber,
            taxCode: staffData.taxCode,
            socialInsurance: staffData.socialInsuranceNumber || '',
            socialInsuranceTime: staffData.socialInsuranceJoinDate || '',
            bankBranch: bank?.branchName || '',
            bankName: bank?.bankName || '',
            bankNumber: bank?.accountNumber || '',
            amountReceived: json.data.salaryData.income,
            eat: salaryData.eat,
            idCardIssuedAt: staffData.idCardIssuedAt || '',
            idCardIssuedBy: staffData.idCardIssuedBy || '',
            goFar: salaryData.goFar,
            order: 0,
            ot: salaryData.ot,
            phoneAllowance: salaryData.phoneAllowance,
            province: bank?.province || '',
            setup: salaryData.setup,
            stay: salaryData.stay,
            warehouse: salaryData.warehouse,
            survey: salaryData.survey,
            totalPrice: staffData.totalSalary,
            training: salaryData.training,
            unitPrice: salaryData.baseSalary,
            workedDay: staffData.totalWorkingDay || 0,
            workOnHoliday: salaryData.workOnHoliday,
            workedShift: staffData.totalShiftCount,
            salaryWarning: staffData.salaryWarning,
            salaryWarningYear: staffData.salaryWarningYear,
            supportAnother: salaryData.supportAnother,
            note: salaryData.note
          });
        }
      }
    });

    usersRef.current = rowData;
    return rowData;

  }, [jsonFile, loadedJSonFileRef.current]);

  const columns: TableColumnsType<CalculateSalaryDataType> = useMemo(() => [
    {
      title: 'STT',
      dataIndex: 'key',
      fixed: 'left',
      width: 20,
      align: 'center',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Họ và tên',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 68,
      render: (_value: any, _record) => {
        return (
          <Space>
            {_value}
            {
              (_record.salaryWarning || _record.salaryWarningYear) && (
                <Tooltip
                  title={<div dangerouslySetInnerHTML={{
                    __html: `
                      <div>${t('salary.didYou')}</div>
                      <ul class="salary_warning-ul">
                        ${_record.salaryWarning ? `<li>${t('salary.exceedingTheMonthlySalaryThreshold')}</li>` : ''}
                        ${_record.salaryWarningYear ? `<li>${t('salary.exceedingTheAnnualSalaryThreshold')}</li>` : ''}
                      </ul>
                `}} />}
                >
                  <WarningOutlined size={30} style={{ color: "#ff3b30" }} />
                </Tooltip>
              )
            }
          </Space>
        )
      }
    },
    {
      title: 'Vị trí',
      dataIndex: 'position',
      key: 'position',
      width: 35,
      align: 'center',
    },
    {
      title: 'Thông tin nhân viên',
      children: [
        {
          title: 'Số ĐT',
          dataIndex: 'phoneNumber',
          key: 'phoneNumber',
          width: 60,
          align: 'center',
        },
        {
          title: 'Số BHXH',
          dataIndex: 'socialInsurance',
          key: 'socialInsurance',
          width: 45,
          align: 'center',
        },
        {
          title: (
            <div>
              Thời gian
              <br />
              đóng BHXH
            </div>
          ),
          dataIndex: 'socialInsuranceTime',
          key: 'socialInsuranceTime',
          width: 50,
          align: 'center',
          render: (_value) => formatDateDDMMYYYY(_value, '/')
        },
        {
          title: 'MST',
          dataIndex: 'taxCode',
          key: 'taxCode',
          width: 60,
          align: 'center',
        },
        {
          title: 'CCCD',
          dataIndex: 'identityNumber',
          key: 'identityNumber',
          width: 60,
          align: 'center',
        },
        {
          title: 'Ngày cấp',
          dataIndex: 'idCardIssuedAt',
          key: 'idCardIssuedAt',
          width: 50,
          align: 'center',
          render: (_value) => formatDateDDMMYYYY(_value, '/')
        },
        {
          title: 'Nơi cấp',
          dataIndex: 'idCardIssuedBy',
          key: 'idCardIssuedBy',
          width: 60,
          align: 'center',
        },
      ],
    },
    {
      title: 'Thông tin chuyển khoản',
      children: [
        {
          title: 'Số tài khoản',
          dataIndex: 'bankNumber',
          key: 'bankNumber',
          width: 100,
          align: 'center',
          render: (bankNumber: any) => bankNumber
        },
        {
          title: 'Ngân hàng',
          dataIndex: 'bankName',
          key: 'bankName',
          width: 100,
          align: 'center',
          render: (bankName: any) => bankName
        },
        {
          title: 'Chi nhánh',
          dataIndex: 'bankBranch',
          key: 'bankBranch',
          width: 100,
          align: 'center',
          render: (bankBranch: any) => bankBranch
        },
        // {
        //   title: 'Tỉnh',
        //   dataIndex: 'province',
        //   key: 'province',
        //   width: 130,
        // },
      ],
    },
    {
      title: 'Số ca',
      dataIndex: 'workedShift',
      key: 'workedShift',
      width: 40,
      align: 'center'
    },
    {
      title: 'Ngày công',
      dataIndex: 'workedDay',
      key: 'workedDay',
      align: 'center',
      width: 40,
    },
    {
      title: 'Đơn giá',
      dataIndex: 'unitPrice',
      key: 'unitPrice',
      width: 40,
      align: 'center',
      render: (value) => <span>{formatNumber(value)}</span>,
    },
    {
      title: 'Thành tiền',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 40,
      align: 'center',
      render: (value) => <span>{formatNumber(value)}</span>,
    },
    {
      title: 'Hỗ trợ',
      children: [
        {
          title: 'Training',
          dataIndex: 'training',
          key: 'training',
          width: 40,
          align: 'center',
          render: (value) => <span>{formatNumber(value)}</span>,
        },
        {
          title: 'Làm ngày lễ',
          dataIndex: 'workOnHoliday',
          key: 'workOnHoliday',
          width: 40,
          align: 'center',
          render: (value) => <span>{formatNumber(value)}</span>,
        },
        {
          title: 'Khảo Sát',
          dataIndex: 'survey',
          key: 'survey',
          width: 40,
          align: 'center',
          render: (value) => <span>{formatNumber(value)}</span>,
        },
        {
          title: 'Hỗ trợ ĐT',
          dataIndex: 'phoneAllowance',
          key: 'phoneAllowance',
          width: 40,
          align: 'center',
          render: (value) => <span>{formatNumber(value)}</span>,
        },
        {
          title: 'Đi xa',
          dataIndex: 'goFar',
          key: 'goFar',
          width: 40,
          align: 'center',
          render: (value) => <span>{formatNumber(value)}</span>,
        },
        {
          title: 'Setup',
          dataIndex: 'setup',
          key: 'setup',
          width: 40,
          align: 'center',
          render: (value) => <span>{formatNumber(value)}</span>,
        },
        {
          title: 'Ở',
          dataIndex: 'stay',
          key: 'stay',
          width: 40,
          align: 'center',
          render: (value) => <span>{formatNumber(value)}</span>,
        },
        {
          title: 'Ăn',
          dataIndex: 'eat',
          key: 'eat',
          width: 40,
          align: 'center',
          render: (value) => <span>{formatNumber(value)}</span>,
        },
        {
          title: 'Thêm giờ',
          dataIndex: 'ot',
          key: 'ot',
          width: 40,
          align: 'center',
          render: (value) => <span>{formatNumber(value)}</span>,
        },
        {
          title: 'Kho',
          dataIndex: 'warehouse',
          key: 'warehouse',
          width: 40,
          align: 'center',
          render: (value) => <span>{formatNumber(value)}</span>,
        },
        {
          title: 'Hỗ trợ khác',
          dataIndex: 'supportAnother',
          key: 'supportAnother',
          width: 50,
          align: 'center',
          render: (value) => <span>{formatNumber(value)}</span>,
        },
        {
          title: <div dangerouslySetInnerHTML={{ __html: t('salary.otherNote') }} />,
          width: 50,
          key: 'otherNote',
          align: 'center',
          render: (_value: string, _record, index) => (
            <Space>
              <Tooltip title={t('salary.note')}>
                <Button
                  icon={_record.note ? <FileDoneOutlined style={{ color: '#f69320' }} /> : <BookOutlined />}
                  onClick={() => {
                    setIsNoteOpen(true);
                    methodNote.reset({
                      note: _record.note || '',
                      id: _record.id
                    });
                  }}
                />
              </Tooltip>
            </Space>
          ),
        },
      ],
    },
    {
      title: 'Thực nhận',
      dataIndex: 'amountReceived',
      key: 'amountReceived',
      width: 45,
      align: 'center',
      fixed: 'right',
      render: (value) => <span>{formatNumber(value)}</span>,
    },
  ], [t]);

  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams.toString());
    setSearchParams(newSearchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      loading={isLoadingJsonFile}
      title={(
        <Typography.Title level={4} style={{ textAlign: 'center' }}>
          {t('salary.tableSalaryDetail')}
        </Typography.Title>
      )}
    >
      <Table<CalculateSalaryDataType>
        columns={columns}
        dataSource={tableData}
        bordered
        scroll={{ x: 4200, y: 60 * 15 }}
        pagination={false}
        rowClassName={(row) => `${row.salaryWarning || row.salaryWarningYear ? 'salary_warning ' : ''}salary_row`}
        summary={(records) => {
          const summaryFieldsNumber = [
            'workedShift',
            'workedDay',
          ];

          const summaryFieldsAmount = [
            'unitPrice',
            'totalPrice',
            'training',
            'workOnHoliday',
            'survey',
            'phoneAllowance',
            'goFar',
            'setup',
            'stay',
            'eat',
            'ot',
            'warehouse',
            'supportAnother',
            'note',
            'amountReceived'
          ];

          const summaryValuesNumber: Record<string, number> = summaryFieldsNumber.reduce((acc, field) => {
            acc[field] = records.reduce((sum, item) => {
              const value = Number((item as any)[field]);
              return sum + (isNaN(value) ? 0 : value);
            }, 0);
            return acc;
          }, {} as Record<string, number>);

          const summaryValuesAmmount: Record<string, number> = summaryFieldsAmount.reduce((acc, field) => {
            acc[field] = records.reduce((sum, item) => {
              const value = Number((item as any)[field]);
              return sum + (isNaN(value) ? 0 : value);
            }, 0);
            return acc;
          }, {} as Record<string, number>);

          return (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                    Tổng:
                  </Typography.Text>
                </Table.Summary.Cell>
                {
                  Array(11).fill(null).map((_, index) => (
                    <Table.Summary.Cell index={2 + index} />
                  ))
                }
                {
                  summaryFieldsNumber.map((field, index) => (
                    <Table.Summary.Cell index={13 + index} align="center">
                      <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                        {summaryValuesNumber[field]}
                      </Typography.Text>
                    </Table.Summary.Cell>
                  ))
                }
                {
                  summaryFieldsAmount.map((field, index) => (
                    <Table.Summary.Cell index={15 + index} align="center">
                      <Typography.Text style={{ fontWeight: 700, fontSize: 14 }}>
                        {formatNumber(summaryValuesAmmount[field])}
                      </Typography.Text>
                    </Table.Summary.Cell>
                  ))
                }
                <Table.Summary.Cell align="center" index={summaryFieldsNumber.concat(summaryFieldsAmount).length + 8 + 1 + 5} />
              </Table.Summary.Row>
            </Table.Summary>
          )
        }}
      />
      <Modal
        open={isNoteOpen}
        onCancel={() => {
          setIsNoteOpen(false);
          methodNote.reset({
            note: '',
            id: null
          });
        }}
        footer={false}
        width={800}
        title={(
          <Typography.Title
            level={4}
          >
            {t('salary.otherNotePopup')}
          </Typography.Title>
        )}
      >
        <FormProvider {...methodNote}>
          <Controller
            name="note"
            render={({
              field: { value, onChange }, fieldState: { error }
            }) => (
              <>
                <TextArea
                  className="u-mt-8"
                  value={value}
                  onChange={onChange}
                  size="large"
                  rows={6}
                  style={{ minHeight: 50 }}
                  readOnly
                />
                {error && (
                  <span
                    className="a-input_errorMessage"
                  >
                    {error.message}
                  </span>
                )}
              </>
            )}
          />
        </FormProvider>
      </Modal>
    </Card>
  );
};

export default SalaryCalculatingView;
